/**
 * A group in the record panel: its state in the three words the Groups page
 * uses, its members and the paths it grants as rows, and Open to the page
 * where both are edited.
 */
import { useQuery } from '@tanstack/react-query';

import { getGroup, listGroupCollections, listGroupMembers } from '@optum/api-client/endpoints/groups';

import { type RecordRef, useRecordPanel } from '../../components/shell/record.js';
import {
  RecordActions,
  RecordAsync,
  RecordColumn,
  RecordFacts,
  RecordIdentity,
  RecordOpen,
  RecordRelated,
  RecordSection,
} from '../../components/shell/RecordParts.js';
import { absoluteDate, shortId } from '../../lib/format.js';
import { qk } from '../../lib/query-keys.js';
import { Badge, IconTile, Typography } from '../../ui/finixt.js';
import { groupState } from './GroupsTab.js';

export function GroupRecord({ record }: { record: RecordRef }) {
  const panel = useRecordPanel();
  const orgId = record.orgId ?? '';
  const id = record.id;
  const group = useQuery({
    queryKey: [...qk.groups(orgId), id],
    queryFn: () => getGroup(orgId, id),
  });
  const members = useQuery({
    queryKey: qk.groupMembers(orgId, id),
    queryFn: () => listGroupMembers(orgId, id),
  });
  const collections = useQuery({
    queryKey: qk.groupCollections(orgId, id),
    queryFn: () => listGroupCollections(orgId, id),
  });
  const memberList = members.data ?? [];
  const pathList = collections.data ?? [];
  const state =
    members.data && collections.data ? groupState(memberList.length, pathList.length) : null;

  return (
    <RecordAsync query={group} record={record}>
      {(g) => (
        <RecordColumn>
          <RecordIdentity
            avatar={<IconTile icon="shield_person" size={40} tint="var(--blue-tint)" color="var(--blue)" />}
            eyebrow={
              <>
                <Typography variant="eyebrow">Group</Typography>
                {state ? <Badge tone={state.tone}>{state.label}</Badge> : null}
              </>
            }
            name={g.name}
            subtitle={g.description ?? undefined}
          />
          <RecordSection label="Facts">
            <RecordFacts
              items={[
                { label: 'ID', value: shortId(g.id) },
                { label: 'Members', value: String(members.data ? memberList.length : g.member_count) },
                { label: 'Paths granted', value: collections.data ? String(pathList.length) : '…' },
                { label: 'Created', value: absoluteDate(g.created_at) },
              ]}
            />
          </RecordSection>
          {memberList.length > 0 ? (
            <RecordSection label="Members">
              <RecordRelated
                rows={memberList.slice(0, 8).map((m) => ({
                  icon: 'person',
                  label: m.email,
                  meta: m.username ?? undefined,
                }))}
              />
            </RecordSection>
          ) : null}
          {pathList.length > 0 ? (
            <RecordSection label="Grants">
              <RecordRelated
                rows={pathList.map((c) => ({
                  icon: 'folder_open',
                  label: <span className="mono">{c.path}</span>,
                  meta: `${c.doc_count} docs`,
                  onClick: () => panel.push({ kind: 'collection', id: c.path, orgId }),
                }))}
              />
            </RecordSection>
          ) : null}
          <RecordActions>
            <RecordOpen to={`/orgs/${orgId}/groups`} label="Open on Groups" />
          </RecordActions>
        </RecordColumn>
      )}
    </RecordAsync>
  );
}
